import { notify } from "@general-backoffice/core";
import type { SubscriptionTier, User } from "@investhinks/api-client";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Collapse,
  CustomInput,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import { subscriptionTiersEndpoints, usersEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";
import { isZeroPriceTier } from "../../utils/isZeroPriceTier";

interface DisableVIPModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: User | null;
  onSuccess?: () => void;
}

const DisableVIPModal: React.FC<DisableVIPModalProps> = ({ isOpen, toggle, user, onSuccess }) => {
  const { t } = useTranslation("users", { keyPrefix: "disableVIP" });
  const [billingStartDate, setBillingStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [selectedTierId, setSelectedTierId] = useState("");
  const [isLoadingTiers, setIsLoadingTiers] = useState(false);

  const userId = user?.id;
  const displayName = user?.name || user?.email || `User #${userId}`;
  const currentTierId = user?.subscription_tier?.id || "";

  // Fetch tiers when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoadingTiers(true);
      subscriptionTiersEndpoints
        .getAll()
        .then(({ data }) => {
          setTiers(data || []);
        })
        .catch((err: unknown) => {
          console.error("Failed to fetch tiers:", err);
          setTiers([]);
        })
        .finally(() => {
          setIsLoadingTiers(false);
        });
    }
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setBillingStartDate(moment().format("YYYY-MM-DD"));
      setError(null);
      setConfirmed(false);
      setSelectedTierId(currentTierId ? String(currentTierId) : "");
    }
  }, [isOpen, currentTierId]);

  // Check billing readiness
  const paymentMethods = user?.pm_paymentMethods || [];
  const hasValidPaymentMethod = paymentMethods.some((pm) => pm.pm_isValid);
  const hasBillingData = !!user?.pm_billingData;
  const hasTaxProfile = !!user?.taxProfile?.taxProfileComplete;
  const isManualPayment = !!user?.isManualPayment;

  // Check if selected tier is a zero-price tier (no payment method needed)
  const selectedTier = tiers.find((t) => String(t.id) === String(selectedTierId));
  const selectedTierIsZeroPrice = isZeroPriceTier(selectedTier);

  // Build warnings
  const warnings = [];
  if (!hasValidPaymentMethod && !isManualPayment && !selectedTierIsZeroPrice) {
    warnings.push(t("warningNoPaymentMethod"));
  }
  if (!hasBillingData && !hasTaxProfile) {
    warnings.push(t("warningNoBillingInfo"));
  }
  if (isManualPayment && !selectedTierIsZeroPrice) {
    warnings.push(t("warningManualPayment"));
  }

  const handleSave = async () => {
    if (!billingStartDate || !confirmed || !userId) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data: response } = await usersEndpoints.disableVip({
        userId,
        billingPeriodStart: billingStartDate,
        subscriptionTierId: selectedTierId ? Number(selectedTierId) : undefined,
      });
      const totalSubs = (response.subscriptionsUpdated || 0) + (response.subscriptionsCreated || 0);
      const freePkgs = response.freePeriodPackagesCreated || 0;

      let message = t("successMessage", { count: totalSubs });
      if (freePkgs > 0) {
        message += " " + t("freePackagesCreated", { count: freePkgs });
      }
      notify.success(message);
      onSuccess?.();
    } catch (err: unknown) {
      console.error("Failed to disable VIP:", err);
      setError(getErrorMessage(err, t("errorGeneric")));
    } finally {
      setIsLoading(false);
    }
  };

  // Standard billing cycle length in days (must match backend)
  const BILLING_CYCLE_DAYS = 30;

  // Calculate billing period info matching backend logic (uses UTC to match server)
  const billingPeriodInfo = useMemo(() => {
    if (!billingStartDate) return null;

    const today = moment.utc().startOf("day");
    const requestedStart = moment.utc(billingStartDate).startOf("day");
    const diffDays = requestedStart.diff(today, "days");

    const isFuture = diffDays > 0;
    const isBackdated = diffDays < 0;
    const backdatedDays = isBackdated ? Math.abs(diffDays) : 0;
    const isLongBackdate = backdatedDays > BILLING_CYCLE_DAYS;

    let actualStart;
    let periodEnd;
    let type;

    if (isFuture) {
      // Future date: billing starts today, free packages until requested date
      actualStart = today;
      periodEnd = moment.utc(today).add(1, "month").subtract(1, "millisecond");
      type = "future";
    } else if (isLongBackdate) {
      // Long backdate (>30 days): period end extended to today
      actualStart = requestedStart;
      periodEnd = moment.utc(today).endOf("day");
      type = "longBackdate";
    } else {
      // Today or short backdate (≤30 days): normal period
      actualStart = requestedStart;
      periodEnd = moment.utc(requestedStart).add(1, "month").subtract(1, "millisecond");
      type = isBackdated ? "shortBackdate" : "today";
    }

    return {
      type,
      actualStart,
      periodEnd,
      freeDays: isFuture ? diffDays : 0,
      backdatedDays,
      isLongBackdate,
    };
  }, [billingStartDate]);

  // Quick action handlers
  const handleQuickAction = (monthsOffset: number) => {
    const newDate = moment().add(monthsOffset, "months").format("YYYY-MM-DD");
    setBillingStartDate(newDate);
  };

  const isQuickActionActive = (monthsOffset: number): boolean => {
    if (!billingStartDate) return false;
    const target = moment().add(monthsOffset, "months").format("YYYY-MM-DD");
    return billingStartDate === target;
  };

  const [showCustomDate, setShowCustomDate] = useState(false);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} style={styles.modalHeader}>
        <div style={styles.headerContent}>
          <i className="fas fa-user-minus" style={styles.headerIcon} />
          <span>{t("title")}</span>
        </div>
      </ModalHeader>

      <ModalBody style={styles.modalBody}>
        {/* User Info */}
        <div style={styles.userInfo}>
          <div style={styles.userName}>{displayName}</div>
          <Badge color="warning" style={styles.vipBadge}>
            VIP
          </Badge>
        </div>

        {/* Billing Readiness Status */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("billingReadiness")}</div>
          <div style={styles.statusCard}>
            <div style={styles.statusRow}>
              <span style={styles.statusLabel}>{t("paymentMethod")}:</span>
              <span
                style={
                  hasValidPaymentMethod || isManualPayment || selectedTierIsZeroPrice
                    ? styles.statusGood
                    : styles.statusBad
                }
              >
                {selectedTierIsZeroPrice
                  ? t("statusNotRequired")
                  : hasValidPaymentMethod
                    ? t("statusValid")
                    : isManualPayment
                      ? t("statusManual")
                      : t("statusMissing")}
              </span>
            </div>
            <div style={styles.statusRow}>
              <span style={styles.statusLabel}>{t("billingInfo")}:</span>
              <span style={hasBillingData ? styles.statusGood : styles.statusNeutral}>
                {hasBillingData ? t("statusSet") : t("statusNotSet")}
              </span>
            </div>
            <div style={styles.statusRow}>
              <span style={styles.statusLabel}>{t("taxProfile")}:</span>
              <span style={hasTaxProfile ? styles.statusGood : styles.statusNeutral}>
                {hasTaxProfile ? t("statusComplete") : t("statusIncomplete")}
              </span>
            </div>
          </div>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div style={styles.section}>
            <Alert color="warning" style={styles.warningAlert}>
              <div style={styles.warningHeader}>
                <i className="fas fa-exclamation-triangle" style={styles.warningIcon} />
                <strong>{t("warningTitle")}</strong>
              </div>
              <ul style={styles.warningList}>
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </Alert>
          </div>
        )}

        {/* Subscription Tier */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("subscriptionTier")}</div>
          <Input
            type="select"
            value={selectedTierId}
            onChange={(e) => setSelectedTierId(e.target.value)}
            style={styles.select}
            disabled={isLoadingTiers}
          >
            <option value="">{t("noTier")}</option>
            {tiers.map((tier) => (
              <option key={tier.id} value={tier.id}>
                {tier.name || tier.code}
                {tier.isDefault ? ` (${t("default")})` : ""}
              </option>
            ))}
          </Input>
          <div style={styles.tierHint}>{t("tierHint")}</div>
        </div>

        {/* Billing Start Date - Only show if tier has billing */}
        {!selectedTierIsZeroPrice && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{t("billingPeriodStart")}</div>

            {/* Quick Actions */}
            <div style={styles.quickActionsGrid}>
              <button
                onClick={() => handleQuickAction(0)}
                style={{
                  ...styles.quickActionButton,
                  ...(isQuickActionActive(0) ? styles.quickActionButtonActive : {}),
                }}
              >
                <span style={styles.quickActionLabel}>{t("today")}</span>
              </button>
              {[1, 3, 6].map((months) => (
                <button
                  key={months}
                  onClick={() => handleQuickAction(months)}
                  style={{
                    ...styles.quickActionButton,
                    ...(isQuickActionActive(months) ? styles.quickActionButtonActiveGreen : {}),
                  }}
                >
                  <span style={styles.quickActionPlus}>+</span>
                  <span style={styles.quickActionNumber}>{months}</span>
                  <span style={styles.quickActionLabel}>{months === 1 ? t("month") : t("months")}</span>
                </button>
              ))}
            </div>

            {/* Custom Date Toggle */}
            <div style={styles.customDateToggle}>
              <Button
                color="link"
                size="sm"
                onClick={() => setShowCustomDate(!showCustomDate)}
                style={styles.customDateButton}
              >
                <i className={`fas fa-chevron-${showCustomDate ? "up" : "down"} mr-2`} />
                {t("customDate")}
              </Button>
            </div>

            <Collapse isOpen={showCustomDate}>
              <Input
                type="date"
                value={billingStartDate || ""}
                onChange={(e) => setBillingStartDate(e.target.value)}
                style={styles.dateInput}
              />
            </Collapse>

            {/* Billing Date Preview */}
            {billingStartDate && billingPeriodInfo && (
              <>
                {billingPeriodInfo.type === "shortBackdate" && (
                  <div style={styles.backdatePreview}>
                    <i className="fas fa-history" style={styles.backdateIcon} />
                    <span>{t("backdatedBy", { days: billingPeriodInfo.backdatedDays })}</span>
                  </div>
                )}
                {billingPeriodInfo.type === "longBackdate" && (
                  <div style={styles.longBackdatePreview}>
                    <i className="fas fa-calendar-alt" style={styles.longBackdateIcon} />
                    <span>{t("longBackdate", { days: billingPeriodInfo.backdatedDays })}</span>
                  </div>
                )}
                {billingPeriodInfo.type === "future" && (
                  <div style={styles.freePreview}>
                    <i className="fas fa-gift" style={styles.freeIcon} />
                    <span>{t("freeDays", { days: billingPeriodInfo.freeDays })}</span>
                  </div>
                )}
                <div style={styles.periodPreview}>
                  <i className="fas fa-calendar-check" style={styles.previewIcon} />
                  <span>{t("firstBillingPeriod")}: </span>
                  <strong>
                    {billingPeriodInfo.actualStart.format("DD MMM YYYY")} -{" "}
                    {billingPeriodInfo.periodEnd.format("DD MMM YYYY")}
                  </strong>
                </div>
              </>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={styles.errorBanner}>
            <i className="fas fa-exclamation-circle" style={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}

        {/* Confirmation Checkbox */}
        <div style={styles.section}>
          <CustomInput
            type="checkbox"
            id="confirmDisableVIP"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            label={t("confirmationLabel")}
            style={styles.checkbox}
          />
        </div>
      </ModalBody>

      <ModalFooter style={styles.modalFooter}>
        <Button color="secondary" outline onClick={toggle} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <Button
          color="danger"
          onClick={handleSave}
          disabled={!billingStartDate || !confirmed || isLoading}
          style={styles.saveButton}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("disabling")}
            </>
          ) : (
            <>
              <i className="fas fa-user-minus mr-2" />
              {t("disableVIP")}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const styles = {
  modalHeader: {
    borderBottom: "1px solid #e5e7eb",
    padding: "16px 20px",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerIcon: {
    color: "#ef4444",
    fontSize: 18,
  },
  modalBody: {
    padding: "20px",
    maxHeight: "70vh",
    overflowY: "auto",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid #e5e7eb",
  },
  userName: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  vipBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 8px",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statusCard: {
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  statusRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusLabel: {
    fontSize: 13,
    color: "#6b7280",
  },
  statusGood: {
    fontSize: 13,
    fontWeight: 500,
    color: "#10b981",
  },
  statusBad: {
    fontSize: 13,
    fontWeight: 500,
    color: "#ef4444",
  },
  statusNeutral: {
    fontSize: 13,
    fontWeight: 500,
    color: "#f59e0b",
  },
  warningAlert: {
    marginBottom: 0,
  },
  warningHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  warningIcon: {
    color: "#d97706",
  },
  warningList: {
    margin: 0,
    paddingLeft: 20,
  },
  quickActionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(72px, 1fr))",
    gap: 10,
    marginBottom: 12,
  },
  quickActionButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 8px",
    backgroundColor: "#f3f4f6",
    border: "2px solid transparent",
    borderRadius: 10,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  quickActionButtonActive: {
    backgroundColor: "#eff6ff",
    borderColor: "#3b82f6",
  },
  quickActionButtonActiveGreen: {
    backgroundColor: "#ecfdf5",
    borderColor: "#10b981",
  },
  quickActionPlus: {
    fontSize: 12,
    fontWeight: 400,
    color: "#9ca3af",
    marginBottom: -2,
  },
  quickActionNumber: {
    fontSize: 20,
    fontWeight: 700,
    color: "#111827",
  },
  quickActionLabel: {
    fontSize: 11,
    color: "#6b7280",
    textTransform: "uppercase",
  },
  customDateToggle: {
    marginBottom: 8,
  },
  customDateButton: {
    padding: 0,
    fontSize: 13,
    color: "#6b7280",
  },
  dateInput: {
    padding: "10px 12px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #d1d5db",
    marginBottom: 12,
  },
  backdatePreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    marginBottom: 8,
    padding: "10px 14px",
    backgroundColor: "#fef3c7",
    borderRadius: 8,
    fontSize: 13,
    color: "#92400e",
  },
  backdateIcon: {
    color: "#f59e0b",
  },
  longBackdatePreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    marginBottom: 8,
    padding: "10px 14px",
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    fontSize: 13,
    color: "#991b1b",
  },
  longBackdateIcon: {
    color: "#ef4444",
  },
  freePreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    marginBottom: 8,
    padding: "10px 14px",
    backgroundColor: "#ecfdf5",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    color: "#065f46",
  },
  freeIcon: {
    color: "#10b981",
  },
  select: {
    padding: "10px 12px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #d1d5db",
  },
  tierHint: {
    marginTop: 8,
    fontSize: 12,
    color: "#6b7280",
  },
  periodPreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    padding: "10px 14px",
    backgroundColor: "#f0f9ff",
    borderRadius: 8,
    fontSize: 13,
    color: "#0369a1",
  },
  previewIcon: {
    color: "#0ea5e9",
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    fontSize: 13,
    color: "#991b1b",
    marginBottom: 20,
  },
  errorIcon: {
    color: "#ef4444",
  },
  checkbox: {
    cursor: "pointer",
  },
  modalFooter: {
    borderTop: "1px solid #e5e7eb",
    padding: "14px 20px",
    display: "flex",
    // A minWidth-pinned primary button plus its cancel overflowed a 360px
    // footer once the label was translated.
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
    gap: 10,
  },
  saveButton: {
    minWidth: 140,
  },
} as const;

export default DisableVIPModal;
